import { useMutation } from '@apollo/client';

import LearnerProfileDesiredSkills from 'bundles/onboarding-common/queries/LearnerProfileDesiredSkills.graphql';
import UpdateDesiredSkillsOrUnsure from 'bundles/onboarding-common/queries/UpdateDesiredSkillsOrUnsure.graphql';
import type { LearnerProfileDesiredSkillsQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileDesiredSkills';
import type {
  UpdateDesiredSkillsOrUnsureMutation,
  UpdateDesiredSkillsOrUnsureMutationVariables,
} from 'bundles/onboarding-common/queries/__generated__/UpdateDesiredSkillsOrUnsure';
import type { Skill } from 'bundles/onboarding-common/types';

const useUpdateDesiredSkillsOrUnsure = () => {
  const [updateDesiredSkillsMutationOrUnsure] = useMutation<
    UpdateDesiredSkillsOrUnsureMutation,
    UpdateDesiredSkillsOrUnsureMutationVariables
  >(UpdateDesiredSkillsOrUnsure, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    update: (cache, { data }) => {
      if (data?.LearnerProfile_PatchSkills.__typename === 'LearnerProfile_PatchSuccess') {
        const existingData = cache.readQuery<LearnerProfileDesiredSkillsQuery>({
          query: LearnerProfileDesiredSkills,
        });

        if (existingData?.LearnerProfile?.me && data.LearnerProfile_PatchSkills.profile) {
          const updatedDesiredSkills = data.LearnerProfile_PatchSkills.profile.desiredSkills;

          cache.writeQuery<LearnerProfileDesiredSkillsQuery>({
            query: LearnerProfileDesiredSkills,
            data: {
              ...existingData,
              LearnerProfile: {
                ...existingData.LearnerProfile,
                me: {
                  ...existingData.LearnerProfile.me,
                  desiredSkills: updatedDesiredSkills,
                },
              },
            },
          });
        }
      }
    },
  });

  const updateDesiredSkillsOrUnsure = async (skills?: Array<Skill> | null, removeSkills?: boolean) => {
    const isUnsureOfSkills = skills?.some((skill) => skill.type === 'UNSURE') ?? false;
    const desiredSkills = !isUnsureOfSkills && skills ? skills.map((skill) => ({ id: skill.id })) : [];
    const variables: UpdateDesiredSkillsOrUnsureMutationVariables = {
      isUnsureOfSkills: { booleanValue: isUnsureOfSkills },
      desiredSkills: removeSkills ? { remove: {} } : { desiredSkills },
    };

    const { errors, data } = await updateDesiredSkillsMutationOrUnsure({
      variables,
    });

    if (errors) {
      return { success: false, errors: errors.map((error) => error.message) };
    }

    if (data?.LearnerProfile_PatchSkills.__typename === 'LearnerProfile_PatchFailure') {
      return { success: false, errors: data?.LearnerProfile_PatchSkills.errors.map((error) => error.message) };
    }

    return { success: true, errors: [] };
  };

  return {
    updateDesiredSkillsOrUnsure,
  };
};

export default useUpdateDesiredSkillsOrUnsure;
